import * as React from 'react'
import { embedMessageSchema, type EmbedMessage } from '@lib/dto/embed-message.dto'
import type { SigningSessionDto } from '@lib/dto/signing-session.dto'
import { isApiError } from '@/helpers/apiClient'
import { requestErrorMessage } from '@/signing/SignChrome'
import { isFramed, parentOrigin } from '@/signing/frame'

/** The framing page's origin when it's one the organization allows, else null. */
export function embedHost(session: SigningSessionDto): string | null {
  const origin = parentOrigin()
  return origin && session.embedOrigins.includes(origin) ? origin : null
}

type LifecycleMessage = Extract<
  EmbedMessage,
  { type: 'ready' | 'viewed' | 'signed' | 'completed' | 'declined' | 'expired' }
>

/** The lifecycle messages a session warrants, in the order they happen. */
export function lifecycleMessages(session: SigningSessionDto): LifecycleMessage[] {
  const base = { source: 'pixelsign' as const, envelopeId: session.envelope.id }
  const messages: LifecycleMessage[] = [{ ...base, type: 'ready' }]
  if (session.gate === 'ready') {
    messages.push({ ...base, type: 'viewed' })
  }
  if (session.signer.status === 'signed') {
    messages.push({ ...base, type: session.envelope.status === 'completed' ? 'completed' : 'signed' })
  }
  if (session.gate === 'unavailable' && session.signer.status === 'declined') {
    messages.push({ ...base, type: 'declined' })
  }
  if (session.gate === 'unavailable' && session.envelope.status === 'expired') {
    messages.push({ ...base, type: 'expired' })
  }
  return messages
}

/**
 * Tells the host page what happens in the embedded signing page: each
 * lifecycle message once, the content height whenever it changes, and returns
 * a reporter for failed actions. Does nothing unless the page is embedded and
 * its parent is an allowed origin.
 */
export function useEmbedHost(
  embedded: boolean,
  session: SigningSessionDto | undefined,
): (error: Error) => void {
  const host = embedded && session ? embedHost(session) : null
  const envelopeId = session?.envelope.id
  const sentRef = React.useRef(new Set<string>())

  React.useEffect(() => {
    if (!host || !session) {
      return
    }
    for (const message of lifecycleMessages(session)) {
      if (!sentRef.current.has(message.type)) {
        sentRef.current.add(message.type)
        window.parent.postMessage(embedMessageSchema.parse(message), host)
      }
    }
  }, [host, session])

  React.useEffect(() => {
    if (!host || !envelopeId) {
      return
    }
    let last = -1
    const observer = new ResizeObserver(() => {
      const height = Math.ceil(window.document.body.getBoundingClientRect().height)
      if (height !== last) {
        last = height
        window.parent.postMessage(
          embedMessageSchema.parse({ source: 'pixelsign', type: 'resize', envelopeId, height }),
          host,
        )
      }
    })
    observer.observe(window.document.body)
    return () => observer.disconnect()
  }, [host, envelopeId])

  return React.useCallback(
    (error: Error) => {
      if (!host || !envelopeId) {
        return
      }
      window.parent.postMessage(
        embedMessageSchema.parse({
          source: 'pixelsign',
          type: 'error',
          envelopeId,
          code: isApiError(error) ? error.body.code ?? null : null,
          message: requestErrorMessage(error),
        }),
        host,
      )
    },
    [host, envelopeId],
  )
}

/**
 * Opens dialogs where the signer is looking when the page is framed. A host
 * that sizes the frame to `resize` leaves the page no scrolling of its own, so
 * a centered dialog lands mid-document, off the host's screen. This records
 * the signer's last pointer or focus, which was on screen, for index.css to
 * center dialogs on; an open dialog stays put.
 */
export function useFramedDialogs(embedded: boolean): void {
  React.useEffect(() => {
    if (!embedded || !isFramed()) {
      return
    }
    const root = window.document.documentElement
    function anchorAt(y: number): void {
      if (root.querySelector('[data-slot="dialog-content"]')) {
        return
      }
      root.style.setProperty('--frame-anchor-y', `${String(Math.round(y))}px`)
    }
    function onPointerDown(event: PointerEvent): void {
      anchorAt(event.clientY)
    }
    function onFocusIn(event: FocusEvent): void {
      if (event.target instanceof Element) {
        const rect = event.target.getBoundingClientRect()
        anchorAt(rect.top + rect.height / 2)
      }
    }
    root.setAttribute('data-frame-anchored', '')
    window.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('focusin', onFocusIn, true)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('focusin', onFocusIn, true)
      root.removeAttribute('data-frame-anchored')
      root.style.removeProperty('--frame-anchor-y')
    }
  }, [embedded])
}
